import type { Attachment, ChatParticipant } from '../common'
import type { UseQueryResult } from '@tanstack/react-query'

import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useMemo, useRef } from 'react'
import { LuCheckCheck } from 'react-icons/lu'
import { MdBrokenImage, MdDownload } from 'react-icons/md'
import { animated, useTransition } from 'react-spring'

import { BASE_URL } from '../../../lib/http'
import { getIdToken } from '../../../lib/token-utils'
import { getDocument, getImage } from '../../chat/actions'
import { CircularProgress } from '../../shared/NarusMaterial'
import { tagClassNameMapper, tagNameMapper } from '../common'

export interface MessageProps extends React.HTMLAttributes<HTMLDivElement> {
  author?: ChatParticipant
  name: string
  isAuthor: boolean
  isPatient: boolean
  isBeingSent?: boolean
  sentAt: Date
  readAt?: Date
  attachment?: Attachment
  tags?: string[]

  patientId: string
  participantId: string
  chatId: string
  clientId: string

  children: React.ReactNode
  onPreviewClick?: () => void
}

interface PreviewResponse {
  data: string
}

interface PreviewData {
  src: string
}

interface QueryParams {
  patientId: string
  chatId: string
  attachmentId?: string
  participantId: string
  clientId: string
}

type QueryKey = [
  'chats/attachment-preview',
  QueryParams
]

function getErrorMessage (err: unknown): string | undefined {
  if (err instanceof Error) {
    return err.message
  }

  interface WithMessage {
    message: string
  }

  function isWithMessage (input: unknown): input is WithMessage {
    return (
      typeof err === 'object' &&
      err !== null &&
      'message' in err &&
      typeof (err as WithMessage).message === 'string'
    )
  }

  if (isWithMessage(err)) {
    return err.message
  }
}

interface usePreviewQueryParams {
  chatId: string
  clientId: string
  patientId: string
  participantId: string
  attachment?: Attachment | null
}

export function usePreviewQuery (params: usePreviewQueryParams): UseQueryResult<PreviewData | undefined, unknown> {
  const { chatId, clientId, patientId, participantId, attachment } = params

  return useQuery<unknown, unknown, PreviewData | undefined, QueryKey>({
    queryKey: [
      'chats/attachment-preview',
      {
        patientId,
        chatId,
        attachmentId: attachment?.id,
        participantId,
        clientId
      }
    ],

    async queryFn ({ queryKey }) {
      let [ , { patientId, chatId, attachmentId, participantId, clientId }] = queryKey

      if (!attachment) {
        return
      }

      let meta = {
        participantId,
        id: attachmentId,
        chatId,
        size: attachment.size,
        filename: attachment.filename,
        mime_type: attachment.mime_type
      }

      if (attachment.mime_type === 'application/pdf') {
        if (attachment.preview) {
          return { src: attachment.preview }
        }

        let response: PreviewResponse = await getDocument(patientId, chatId, attachment.id, meta, clientId, 'MessageAttachment')
        return { src: response.data }
      }

      let response: PreviewResponse = await getImage(patientId, chatId, attachment.id, meta, clientId)
      return { src: response.data }
    },

    enabled: !!attachment,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })
}

export function MessageComponent ({
  author,
  name,
  isAuthor,
  isPatient,
  isBeingSent = false,
  sentAt,
  readAt,
  attachment,
  participantId,
  patientId,
  chatId,
  clientId,
  children,
  tags,
  className,
  onPreviewClick,
  ...rest
}: MessageProps): JSX.Element {
  let ref = useRef<HTMLDivElement>(null)

  let previewQuery = usePreviewQuery({
    participantId,
    patientId,
    chatId,
    clientId,
    attachment
  })

  return (
    <div
      ref={ref}
      className={cx(
        'flex flex-row py-2 px-4 border-red-400',
        isAuthor && 'justify-end pl-8',
        !isAuthor && 'justify-start',
        previewQuery.status === 'pending' && previewQuery.fetchStatus === 'fetching' && 'w-full',
        className
      )}
      {...rest}
    >
      <div
        className={cx(
          'flex flex-col border-blue-400',
          isAuthor && 'items-end',
          !isAuthor && 'items-start'
        )}
      >
        {Array.isArray(tags) && tags.length > 0 && (
          <div className='flex flex-row flex-wrap no-scrollbar pb-3 gap-2 items-start justify-start'>
            {tags.map(tag => (
              <div
                key={tag}
                className={cx(
                  'whitespace-nowrap p-1 px-1.5 text-xs font-medium font-roboto',
                  tagClassNameMapper[tag] ?? 'text-slate-600 bg-gray-100'
                )}
              >
                {tagNameMapper[tag] ?? tag}
              </div>
            ))}
          </div>
        )}

        <div className='flex flex-row border-slate-400'>
          <div
            className={cx(
              'rounded-xl p-4 flex flex-col gap-2 relative overflow-hidden border-green-400',
              !isPatient && isAuthor && 'bg-[#eee9fb]',
              !isPatient && !isAuthor && 'bg-gray-100',
              isPatient && 'bg-[#ffeec1]',
              previewQuery.status === 'pending' && previewQuery.fetchStatus === 'fetching' && 'w-full'
            )}
          >
            <div className='flex flex-col gap-2'>
              {previewQuery.status === 'error' && (
                <div className='w-full flex justify-center items-center gap-2 p-2 border border-red-500 rounded-t-lg rounded-b'>
                  <button
                    type='button'
                    className='p-2 -m-2'
                    title='Retry loading attachment preview'
                    onClick={() => {
                      void previewQuery.refetch()
                    }}
                  >
                    <MdBrokenImage
                      size={24}
                      className='text-red-600'
                    />
                  </button>

                  <p className='font-inter text-xs text-red-600'>
                    {getErrorMessage(previewQuery.error) ?? 'An error occurred while loading the attachment preview'}
                  </p>
                </div>
              )}

              {previewQuery.status === 'pending' && previewQuery.fetchStatus === 'fetching' && (
                <div className='w-full h-24 flex justify-center items-center'>
                  <CircularProgress
                    type='small'
                    className='[&_.spinner-layer]:border-blue-400'
                  />
                </div>
              )}

              {previewQuery.status === 'success' && previewQuery.data?.src && (
                <div className='w-full max-h-24 overflow-hidden rounded-lg relative min-h-[2rem]'>
                  { }
                  <img
                    src={previewQuery.data.src}
                    alt={attachment?.filename}
                    className={cx('object-cover object-center', typeof onPreviewClick === 'function' && 'cursor-pointer')}
                    onClick={onPreviewClick}
                  />

                  <button
                    type='button'
                    className='absolute top-0 right-0 bg-white bg-opacity-50 hover:bg-opacity-75 rounded-full p-1 m-1 text-gray-500 hover:text-blue-500 transition-colors'
                    title={`Download ${attachment?.filename ?? 'attachment'}`}
                    onClick={() => {
                      async function download (): Promise<void> {
                        let token: string = await getIdToken()
                        let url = `${BASE_URL}/patients/${patientId}/messages/${chatId}/attachment/${attachment?.id ?? ''}?participant=${participantId}&clientName=${encodeURIComponent(clientId)}&downloadType=attachment&authorization=${encodeURIComponent(`Bearer ${token}`)}`
                        window.open(url, '_blank')
                      }

                      void download()
                    }}
                  >
                    <MdDownload
                      size={20}
                      className=''
                    />
                  </button>
                </div>
              )}

              {typeof children === 'string' && (
                <>
                  {children.split(/\n+/g).map((line, index) => (
                    <p
                      key={index}
                      className='font-inter text-sm break-words'
                      style={{}}
                    >
                      {line}
                    </p>
                  ))}
                </>
              )}
            </div>

            <div className='flex flex-row justify-between gap-4'>
              <p
                className='font-inter text-xs text-gray-500 line-clamp-1'
                title={name}
              >
                {name}
              </p>

              <div className='flex flex-row gap-2 items-center'>
                {readAt && !isNaN(readAt.getTime()) && (
                  <div
                    className='flex justify-center items-center text-blue-600 p-1 -m-1'
                    title={`Read at ${readAt.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}`}
                  >
                    <LuCheckCheck
                      size={14}
                      className='pointer-events-none'
                    />
                  </div>
                )}

                {sentAt && !isNaN(sentAt.getTime()) && (
                  <span
                    className='font-inter text-xs text-gray-500 p-1 -m-1'
                    title={`Sent at ${sentAt.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}`}
                  >
                    {sentAt.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                )}
              </div>
            </div>

            <ProgressBar isVisibile={isBeingSent} />
          </div>
        </div>
      </div>

    </div>
  )
}

interface ProgressBarProps {
  isVisibile: boolean
}

function ProgressBar ({ isVisibile }: ProgressBarProps): JSX.Element {
  let value = useMemo(() => isVisibile ? [ '1' ] : [], [ isVisibile ])

  let transition = useTransition(value, {
    from: {
      opacity: 0,
      transform: 'scaleX(0)'
    },
    enter: {
      opacity: 0.5,
      transform: 'scaleX(1)'
    },
    leave: {
      opacity: 0,
      transform: 'scaleX(1)'
    }
  })

  return transition((style, item) => (
    <animated.div
      className='h-[2px] w-full bg-blue-500 absolute left-0 bottom-0 origin-left'
      style={style}
    />
  ))
}
